import React from 'react'
import { Link } from 'react-router-dom'
import {
    Box,
    Drawer,
    Typography,
    IconButton,
    Divider,
    styled,
} from '@mui/material'
import { lighten, Theme, useTheme } from '@mui/material/styles'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faHome } from '@fortawesome/pro-duotone-svg-icons/faHome'
import { faChevronLeft } from '@fortawesome/pro-duotone-svg-icons/faChevronLeft'

import MainMenuItems from '@/feature/navigation/Sidemenu'
import { CompactButton } from '@/Components/Elements/StyledComponents/Buttons'
import { useEnvironmentInfo } from '@/feature/navigation/Sidemenu/hooks/useEnvironmentInfo'

export const APP_NAVIGATION_DRAWER_WIDTH = 280

const DrawerHeader = styled('div')(({ theme }: { theme: Theme }) => ({
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(1),
    ...theme.mixins.toolbar,
}))

interface AppNavigationDrawerProps {
    hasEnvironment: boolean
    navPresent: boolean
    menuOpen: boolean
    isMobile: boolean
    history: any
    kedo: any
    onMenuStateChange: (isOpen: boolean) => void
    closeMenu: () => void
}

const AppNavigationDrawer: React.FC<AppNavigationDrawerProps> = ({
    hasEnvironment,
    navPresent,
    menuOpen,
    isMobile,
    history,
    kedo,
    onMenuStateChange,
    closeMenu,
}) => {
    const theme = useTheme()

    const {
        environment,
        environmentName,
        logoSrc,
        handleImageError,
        clearEnvironment,
    } = useEnvironmentInfo(kedo)

    if (!hasEnvironment) {
        return (
            <Link
                to="/environment"
                style={{ textDecoration: 'none' }}
                onClick={() => {
                    localStorage.removeItem('environment')
                    kedo.env().clearInfo()
                }}
            >
                <CompactButton
                    data-cy="menu-home-button"
                    sx={{
                        position: 'fixed',
                        left: 16,
                        top: 16,
                        zIndex: theme.zIndex.drawer + 1,
                    }}
                >
                    <FontAwesomeIcon icon={faHome} />
                </CompactButton>
            </Link>
        )
    }

    if (!navPresent) {
        return null
    }

    return (
        <>
            <Drawer
                id="sidebar-main-menu"
                data-cy="menu-drawer"
                variant={isMobile ? 'temporary' : 'persistent'}
                open={menuOpen}
                onClose={() => onMenuStateChange(false)}
                ModalProps={{
                    keepMounted: true,
                }}
                sx={{
                    width:
                        !isMobile && menuOpen ? APP_NAVIGATION_DRAWER_WIDTH : 0,
                    flexShrink: 0,
                    transition: theme.transitions.create('width', {
                        easing: menuOpen
                            ? theme.transitions.easing.easeOut
                            : theme.transitions.easing.sharp,
                        duration: menuOpen
                            ? theme.transitions.duration.enteringScreen
                            : theme.transitions.duration.leavingScreen,
                    }),
                    '& .MuiDrawer-paper': {
                        width: APP_NAVIGATION_DRAWER_WIDTH,
                        boxSizing: 'border-box',
                        backgroundColor: 'background.paper',
                        color: 'text.primary',
                        ...(!isMobile &&
                            !menuOpen && {
                                overflow: 'hidden',
                                transition: theme.transitions.create('width', {
                                    easing: theme.transitions.easing.sharp,
                                    duration:
                                        theme.transitions.duration
                                            .leavingScreen,
                                }),
                            }),
                        ...(!isMobile &&
                            menuOpen && {
                                transition: theme.transitions.create('width', {
                                    easing: theme.transitions.easing.easeOut,
                                    duration:
                                        theme.transitions.duration
                                            .enteringScreen,
                                }),
                            }),
                    },
                }}
            >
                <DrawerHeader>
                    <Box
                        component={Link}
                        to="/environment"
                        title={kedo.t('switch_environment')}
                        onClick={clearEnvironment}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            minWidth: 0,
                            flex: 1,
                            pr: 5,
                            textDecoration: 'none',
                            color: 'inherit',
                        }}
                    >
                        <Box
                            sx={{
                                width: 52,
                                height: 52,
                                boxSizing: 'border-box',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                                overflow: 'hidden',
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'inline-block',
                                    width: '100%',
                                    height: '100%',
                                    maxWidth: 52,
                                    maxHeight: 52,
                                    objectFit: 'cover',
                                    borderRadius: 2,
                                }}
                                component="img"
                                onError={handleImageError}
                                className="avatar"
                                src={logoSrc}
                                alt={`Logo van ${environment?.name ?? ''}`}
                            />
                        </Box>

                        <Typography
                            component="h6"
                            sx={{
                                textTransform: 'capitalize',
                                fontWeight: 'bold',
                                minWidth: 0,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {environmentName.slice(0, 25)}
                        </Typography>
                    </Box>
                    <IconButton
                        data-cy="menu-close-button"
                        onClick={() => onMenuStateChange(!menuOpen)}
                        size="small"
                        title={kedo.t('Collapse menu')}
                        sx={{
                            position: 'absolute',
                            right: 8,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            zIndex: theme.zIndex.drawer + 1,
                            transition: theme.transitions.create(['left'], {
                                easing: theme.transitions.easing.sharp,
                                duration:
                                    theme.transitions.duration.leavingScreen,
                            }),
                            color: 'primary.dark',
                            backgroundColor: lighten(
                                theme.palette.primary.light,
                                0.9
                            ),
                            '&:hover': {
                                backgroundColor: lighten(
                                    theme.palette.primary.light,
                                    0.8
                                ),
                            },
                            '& svg': {
                                fontSize: 16,
                                width: 16,
                            },
                        }}
                    >
                        <FontAwesomeIcon icon={faChevronLeft} />
                    </IconButton>
                </DrawerHeader>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        height: '100%',
                        overflow: 'hidden',
                        mt: 1,
                    }}
                >
                    <Box
                        className="mainMenuFlex"
                        sx={{
                            flex: 1,
                            overflow: 'auto',
                            pb: 2,
                        }}
                    >
                        <MainMenuItems closing={closeMenu} kedo={kedo} />
                    </Box>

                    <Box
                        className="mainMenuFlex releaseInfo"
                        sx={{
                            p: 2,
                            borderTop: 1,
                            borderColor: 'divider',
                            textAlign: 'center',
                        }}
                    >
                        <Typography variant="caption" color="text.secondary">
                            Celestial Mirage {(window as any).appVersion}
                        </Typography>
                    </Box>
                </Box>
            </Drawer>
        </>
    )
}

export default AppNavigationDrawer
